/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Checkbox,
  ListItem,
  ListItemIcon,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { CustomIconButton } from '@opengeoweb/shared';
import { OverflowTooltip } from './OverflowTooltip';
import { useTimeseriesTranslation } from '../../../utils/i18n';

export interface LocationListItemProps {
  location: string;
  handleChange: (newSelectedLocation: string) => void;
  selectedLocation: string;
  isDisabled?: boolean;
}

export const LocationListItem: React.FC<LocationListItemProps> = ({
  location,
  handleChange,
  selectedLocation,
  isDisabled,
}) => {
  const theme = useTheme();
  const { t } = useTimeseriesTranslation();
  // Wrapper ref used for hover detection AND restoring keyboard focus after selection.
  const mouseMoveRef = React.useRef<HTMLDivElement>(null);
  // Listen for global selection events so we can re-focus the item that was activated via keyboard.
  React.useEffect(() => {
    const handleSelection = (e: Event): void => {
      const custom = e as CustomEvent<{ location?: string }>;
      if (custom.detail?.location === location && mouseMoveRef.current) {
        // Delay to allow parent state updates / virtualization re-render.
        requestAnimationFrame(() => {
          mouseMoveRef.current?.focus();
        });
      }
    };
    window.addEventListener('location-list-selection', handleSelection);
    return (): void => {
      window.removeEventListener('location-list-selection', handleSelection);
    };
  }, [location]);
  const textContainerRef = React.useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = React.useState(false);
  const disableONLYUntillImplementation = true;

  const handleMouseEnter = (): void => {
    setIsHovering(true);
  };
  const handleMouseLeave = (): void => {
    setIsHovering(false);
  };
  const selectOnlyOne = (): void => {};

  const checkHover = (event: MouseEvent): void => {
    if (mouseMoveRef.current) {
      const mouseOver = mouseMoveRef.current.contains(event.target as Node);
      if (!isHovering && mouseOver) {
        handleMouseEnter();
      }
      if (isHovering && !mouseOver) {
        handleMouseLeave();
      }
    }
  };

  React.useEffect(() => {
    window.addEventListener('mousemove', checkHover, true);
    return (): void => {
      window.removeEventListener('mousemove', checkHover, true);
    };
  });

  const [textContainerWidth, setTextContainerWidth] = React.useState(0);

  React.useEffect(() => {
    if (textContainerRef.current) {
      setTextContainerWidth(textContainerRef.current.clientWidth);
    }
  }, [textContainerRef]);

  // If selection state changes and this item is now the selected one, ensure it regains focus.
  React.useEffect(() => {
    if (selectedLocation === location && mouseMoveRef.current) {
      // If focus already on this item, do nothing.
      if (mouseMoveRef.current === document.activeElement) {
        return;
      }
      // If focus is outside the list item, re-focus after render settles.
      // Use double rAF for virtualization/layout stability.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          mouseMoveRef.current?.focus();
        });
      });
    }
  }, [selectedLocation, location]);

  return (
    <OverflowTooltip text={location} textContainerWidth={textContainerWidth}>
      <div
        aria-selected={selectedLocation === location}
        onBlur={handleMouseLeave}
        onFocus={handleMouseEnter}
        onKeyDown={(e): void => {
          if (isDisabled) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleChange(location);
            // Dispatch custom event to remove focus styling from outer list container
            window.dispatchEvent(
              new CustomEvent('location-list-selection', {
                detail: { location },
              }),
            );
            // Explicitly retain focus immediately (backup before effect runs)
            requestAnimationFrame(() => {
              mouseMoveRef.current?.focus();
            });
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={mouseMoveRef}
        role="option"
        tabIndex={0}
      >
        <ListItem
          data-testid="locationDropdownListItem"
          role="listitem"
          {...(!isDisabled && {
            onClick: (): void => handleChange(location),
          })}
          sx={{
            padding: '0px',
            paddingLeft: '0px',
            paddingRight: '50px',
            fontSize: '12px',
            backgroundColor: isHovering
              ? theme.palette.geowebColors.buttons.tool.mouseOver.fill
              : undefined,
          }}
        >
          <ListItemIcon sx={{ minWidth: '42px' }}>
            <Checkbox
              checked={selectedLocation === location}
              color="secondary"
              disabled={isDisabled}
              onChange={() => {
                if (!isDisabled) {
                  handleChange(location);
                  window.dispatchEvent(
                    new CustomEvent('location-list-selection', {
                      detail: { location },
                    }),
                  );
                }
              }}
              slotProps={{
                input: { 'aria-label': location },
              }}
              sx={{
                paddingTop: '6px',
                paddingBottom: '7px',
                '&&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              // Remove from tab order so row wrapper is the single focus target
              tabIndex={-1}
            />
          </ListItemIcon>

          <Typography
            ref={textContainerRef}
            sx={{
              fontSize: '16px',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              width: '75%',
            }}
            variant="body2"
          >
            {location}
          </Typography>

          {/* This feature is disabled untill the implementation continues */}
          {!disableONLYUntillImplementation ? (
            <CustomIconButton
              arial-label={`Only ${location}`}
              edge="end"
              onClick={(): void => selectOnlyOne()}
              style={{ backgroundColor: 'transparent', width: 'auto' }}
            >
              <Typography
                sx={{
                  color: 'geowebColors.buttons.primary.default.fill',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('timeseries-location-dropdown-only')}
              </Typography>
            </CustomIconButton>
          ) : null}
        </ListItem>
      </div>
    </OverflowTooltip>
  );
};
